import type { WriteMap } from "./character-edits.js";

export interface CustomSkill {
  id: string;
  nameKey: string;
  descKey: string;
  usage: readonly ("overcome" | "create-advantage" | "attack" | "defend")[];
}

export interface CustomStunt {
  id: string;
  nameKey: string;
  descKey: string;
  cost: {
    type: "passive" | "fp" | "per_scene" | "per_session" | "conditional";
    amount?: number;
    limit?: number;
    conditionText?: string;
  };
  effectHint?: Record<string, unknown>;
}

export interface CustomExtra {
  id: string;
  name: string;
  kind: "companion" | "gear" | "vehicle" | "power" | "other";
  description?: string;
  tags?: readonly string[];
}

export interface CustomizationRecord {
  skills: {
    added: CustomSkill[];
    removed: string[];
    renamed: Record<string, { nameKey?: string; descKey?: string }>;
    pyramidShape?: unknown;
  };
  stunts: {
    addedLibrary: CustomStunt[];
    removedLibraryIds: string[];
  };
  extras: {
    addedLibrary: CustomExtra[];
    removedLibraryIds: string[];
  };
}

export function addCustomSkill(
  tablePath: string,
  cur: CustomizationRecord,
  skill: CustomSkill,
): WriteMap {
  const added = [...(cur.skills.added ?? []).filter((s) => s.id !== skill.id), skill];
  return { [`${tablePath}/customization/skills/added`]: added };
}

export function removeAddedSkill(
  tablePath: string,
  cur: CustomizationRecord,
  id: string,
): WriteMap {
  const added = (cur.skills.added ?? []).filter((s) => s.id !== id);
  return { [`${tablePath}/customization/skills/added`]: added };
}

export function markSkillRemoved(
  tablePath: string,
  cur: CustomizationRecord,
  id: string,
): WriteMap {
  const existing = cur.skills.removed ?? [];
  if (existing.includes(id)) {
    return { [`${tablePath}/customization/skills/removed`]: existing };
  }
  return { [`${tablePath}/customization/skills/removed`]: [...existing, id] };
}

export function unmarkSkillRemoved(
  tablePath: string,
  cur: CustomizationRecord,
  id: string,
): WriteMap {
  const filtered = (cur.skills.removed ?? []).filter((s) => s !== id);
  return { [`${tablePath}/customization/skills/removed`]: filtered };
}

export function renameSkill(
  tablePath: string,
  _cur: CustomizationRecord,
  id: string,
  patch: { nameKey?: string; descKey?: string },
): WriteMap {
  return { [`${tablePath}/customization/skills/renamed/${id}`]: patch };
}

export function clearSkillRename(tablePath: string, id: string): WriteMap {
  return { [`${tablePath}/customization/skills/renamed/${id}`]: null };
}

export function addLibraryStunt(
  tablePath: string,
  cur: CustomizationRecord,
  stunt: CustomStunt,
): WriteMap {
  const added = [...(cur.stunts.addedLibrary ?? []).filter((s) => s.id !== stunt.id), stunt];
  return { [`${tablePath}/customization/stunts/addedLibrary`]: added };
}

export function removeLibraryStunt(
  tablePath: string,
  cur: CustomizationRecord,
  id: string,
): WriteMap {
  const added = cur.stunts.addedLibrary ?? [];
  const inAdded = added.some((s) => s.id === id);
  if (inAdded) {
    return {
      [`${tablePath}/customization/stunts/addedLibrary`]: added.filter((s) => s.id !== id),
    };
  }
  const removed = cur.stunts.removedLibraryIds ?? [];
  if (removed.includes(id)) {
    return { [`${tablePath}/customization/stunts/removedLibraryIds`]: removed };
  }
  return { [`${tablePath}/customization/stunts/removedLibraryIds`]: [...removed, id] };
}

export function addLibraryExtra(
  tablePath: string,
  cur: CustomizationRecord,
  extra: CustomExtra,
): WriteMap {
  const added = [...(cur.extras.addedLibrary ?? []).filter((e) => e.id !== extra.id), extra];
  return { [`${tablePath}/customization/extras/addedLibrary`]: added };
}

export function removeLibraryExtra(
  tablePath: string,
  cur: CustomizationRecord,
  id: string,
): WriteMap {
  const added = cur.extras.addedLibrary ?? [];
  const inAdded = added.some((e) => e.id === id);
  if (inAdded) {
    return {
      [`${tablePath}/customization/extras/addedLibrary`]: added.filter((e) => e.id !== id),
    };
  }
  const removed = cur.extras.removedLibraryIds ?? [];
  if (removed.includes(id)) {
    return { [`${tablePath}/customization/extras/removedLibraryIds`]: removed };
  }
  return { [`${tablePath}/customization/extras/removedLibraryIds`]: [...removed, id] };
}
